import React, { useState } from 'react';
import { Smartphone, Shield, Settings, Download, Wifi, Battery, Camera, Lock, Clock, ArrowRight, CheckCircle, Star, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import Breadcrumb from './Breadcrumb';
import Footer from './Footer';

const MobilePage = ({ setCurrentSection, onSelectGuide, guides }) => {
  // Function to count procedures in a guide by analyzing internal links
  const countProceduresInGuide = (guideId) => {
    const guide = guides.find(g => g.id === guideId);
    if (!guide || !guide.content || !guide.content.sections) return 0;
    
    let linkCount = 0;
    guide.content.sections.forEach(section => {
      if (section.content) {
        // Count links in format [text](link-id)
        const links = section.content.match(/\[([^\]]+)\]\(([^)]+)\)/g);
        if (links) {
          linkCount += links.length;
        }
      }
    });
    return linkCount;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-100">
      {/* Breadcrumb */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb />
        </div>
      </div>

      {/* Hero Banner */}
      <section className="relative overflow-hidden bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white bg-opacity-20 backdrop-blur-sm rounded-3xl mb-8">
              <Smartphone className="h-12 w-12 text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-6">
              Appareils Mobiles
            </h1>
            <p className="text-xl md:text-2xl text-purple-100 max-w-3xl mx-auto">
              Optimisez votre productivité mobile avec iPhone, iPad, Android et tous vos appareils nomades
            </p>
          </div>
        </div>
        
        {/* Floating elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-purple-300 rounded-full opacity-20 animate-bounce"></div>
        <div className="absolute bottom-20 right-10 w-16 h-16 bg-pink-300 rounded-full opacity-30 animate-pulse"></div>
        <div className="absolute top-1/2 left-1/4 w-12 h-12 bg-rose-300 rounded-full opacity-25 animate-ping"></div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Mobile Platforms Section */}
        <section className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-3xl p-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-pink-400 to-rose-500 rounded-2xl mb-6">
              <Smartphone className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Écosystèmes Mobiles
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Maîtrisez votre appareil mobile avec nos guides spécialisés par plateforme
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                name: 'iPhone & iPad',
                icon: Smartphone,
                description: 'Configuration, apps et astuces iOS',
                gradient: 'from-gray-500 to-gray-600',
                guideId: 'iphone-configuration',
                hasGuides: true,
                category: 'apple'
              },
              {
                name: 'Android',
                icon: Settings,
                description: 'Personnalisation et optimisation',
                gradient: 'from-green-500 to-green-600',
                guideId: 'android-customization',
                hasGuides: true,
                category: 'android'
              },
              {
                name: 'Sécurité Mobile',
                icon: Shield,
                description: 'Protection et confidentialité',
                gradient: 'from-red-500 to-red-600',
                guideId: 'mobile-security',
                hasGuides: false
              },
              {
                name: 'Applications',
                icon: Download,
                description: 'Sélection et gestion d\'apps',
                gradient: 'from-blue-500 to-blue-600',
                guideId: 'mobile-apps',
                hasGuides: false
              },
              {
                name: 'Connectivité',
                icon: Wifi,
                description: 'WiFi, Bluetooth et réseau',
                gradient: 'from-cyan-500 to-cyan-600',
                guideId: 'mobile-connectivity',
                hasGuides: false
              },
              {
                name: 'Photographie',
                icon: Camera,
                description: 'Techniques photo et vidéo mobile',
                gradient: 'from-yellow-500 to-yellow-600',
                guideId: 'mobile-photography',
                hasGuides: false
              }
            ].map((tool, index) => {
              const IconComponent = tool.icon;
              const procedureCount = tool.hasGuides ? countProceduresInGuide(tool.guideId) : 0;
              
              return (
                <Card 
                  key={index} 
                  className="group cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 border-0 overflow-hidden relative h-full flex flex-col"
                  onClick={() => {
                    if (tool.hasGuides && onSelectGuide) {
                      console.log('Mobile Guide selected:', { id: tool.guideId });
                      onSelectGuide({ id: tool.guideId });
                    }
                  }}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${tool.gradient} opacity-5 group-hover:opacity-10 transition-opacity duration-300`}></div>
                  
                  <CardHeader className="text-center pb-4 relative z-10 flex-grow">
                    <div className={`mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br ${tool.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900 mb-2">
                      {tool.name}
                    </CardTitle>
                    <CardDescription className="text-gray-600 leading-relaxed flex-grow">
                      {tool.description}
                    </CardDescription>
                    
                    {tool.hasGuides && procedureCount > 0 && (
                      <div className="mt-4 flex items-center justify-center">
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          {procedureCount} procédure{procedureCount > 1 ? 's' : ''}
                        </Badge>
                      </div>
                    )}
                    
                    {tool.hasGuides && procedureCount === 0 && (
                      <div className="mt-4 flex items-center justify-center">
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          1 guide
                        </Badge>
                      </div>
                    )}
                  </CardHeader>
                  
                  {/* Bottom Action Bar */}
                  <div className={`bg-gray-900 ${tool.hasGuides ? `group-hover:bg-gradient-to-r group-hover:${tool.gradient}` : ''} transition-all duration-300 p-4 relative z-10`}>
                    <Button 
                      variant="ghost"
                      disabled={!tool.hasGuides}
                      className={`w-full font-bold text-lg hover:bg-transparent ${
                        tool.hasGuides 
                          ? 'text-white' 
                          : 'text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {tool.hasGuides ? 'Consulter le guide' : 'Bientôt disponible'}
                      {tool.hasGuides && <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />}
                    </Button>
                  </div>
                  
                  {tool.hasGuides && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-1000 pointer-events-none"></div>
                  )}
                </Card>
              );
            })}
          </div>
        </section>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default MobilePage;